import React, { useState, useEffect, useRef } from 'react';
import { Button, Select, Cascader, message } from 'antd';
import { CloseOutlined, InfoCircleFilled } from '@ant-design/icons';
import intl from 'react-intl-universal';
import { connect } from 'react-redux';
import storage from '@/utils/storage';
import { saveShortcutConfig } from '@/api/setting';
import { updateShortcutConfig } from '@/redux/actions/user';
import {
  SHOP_FUNC_LIST,
  FUNCTION_SHORTCUT_KEYS,
  filterEmptyData,
  handleMenuAuth,
  filterMenuKey,
} from './helper';
import styles from './index.module.scss';

const QuickKey = (props) => {
  const { menus = [], companyGroup = null, shortcuts = null } = props;
  const hasGroupAuth = Boolean(storage.get('isGroupLogin'));
  const hasStoreAuth = Boolean(storage.get('commonCode'));
  const [shopMenuShortcuts, setShopMenuShortcuts] = useState([]); // 门店界面菜单快捷键
  const [shopFuncShortcuts, setShopFuncShortcuts] = useState([]); // 门店界面功能快捷键
  const [groupMenuShortcuts, setGroupMenuShortcuts] = useState([]); // 集团界面菜单快捷键
  const saveTimeoutRef = useRef(null);

  // 初始化加载快捷键配置
  useEffect(() => {
    if (shortcuts) {
      const { menuShortcuts, funcShortcuts, groupShortcuts } = shortcuts;
      if (hasStoreAuth) {
        // 加载门店菜单快捷键  取最新有权限的数据
        setShopMenuShortcuts(menuShortcuts);
        // 加载门店功能快捷键
        setShopFuncShortcuts(funcShortcuts);
      }
      if (hasGroupAuth) {
        // 加载集团菜单快捷键 取最新有权限的数据
        setGroupMenuShortcuts(groupShortcuts);
      }
    }
  }, [shortcuts]);

  // 防抖处理保存快捷键配置
  const saveShortcuts = (type, data) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    let currentShopMenuShortcuts = [...shopMenuShortcuts];
    let currentShopFuncShortcuts = [...shopFuncShortcuts];
    let currentGroupMenuShortcuts = [...groupMenuShortcuts];

    if (type === 'shopMenu') {
      currentShopMenuShortcuts = data;
    } else if (type === 'shopFunc') {
      currentShopFuncShortcuts = data;
    } else if (type === 'groupMenu') {
      currentGroupMenuShortcuts = data;
    }

    props.updateShortcutConfig(
      hasGroupAuth
        ? {
            ...shortcuts,
            groupShortcuts: handleMenuAuth(currentGroupMenuShortcuts, companyGroup?.menus || []),
          }
        : {
            ...shortcuts,
            menuShortcuts: handleMenuAuth(currentShopMenuShortcuts, menus || []),
            funcShortcuts: currentShopFuncShortcuts,
          },
    ); // 更新redux中的快捷键配置

    // 使用当前操作后的最新数据
    const saveParamsData = hasGroupAuth
      ? {
          ...shortcuts,
          groupShortcuts: currentGroupMenuShortcuts,
        }
      : {
          ...shortcuts,
          menuShortcuts: currentShopMenuShortcuts,
          funcShortcuts: currentShopFuncShortcuts,
        };
    saveTimeoutRef.current = setTimeout(() => {
      // 排除空的数据
      const requestData = filterEmptyData(saveParamsData);
      saveShortcutConfig(requestData).then(() => {});
    }, 300);
  };

  // 添加门店菜单快捷键
  const addShopMenuShortcut = () => {
    const newShortcuts = [...shopMenuShortcuts, { menu: undefined, key: undefined }];
    setShopMenuShortcuts(newShortcuts);
  };

  // 删除门店菜单快捷键
  const deleteShopMenuShortcut = (index) => {
    const newShortcuts = [...shopMenuShortcuts];
    newShortcuts.splice(index, 1);
    setShopMenuShortcuts(newShortcuts);
    saveShortcuts('shopMenu', newShortcuts);
  };

  // 更新门店菜单快捷键
  const updateShopMenuShortcut = (index, field, value) => {
    const newShortcuts = [...shopMenuShortcuts];
    newShortcuts[index][field] = value;

    // 如果修改的是快捷键字段，需要检查是否与其他快捷键冲突
    if (field === 'key' && value) {
      // 在门店菜单快捷键中检查冲突
      newShortcuts.forEach((item, idx) => {
        if (idx !== index && item.key === value) {
          newShortcuts[idx].key = undefined;
        }
      });

      // 在门店功能快捷键中检查冲突
      const newFuncShortcuts = [...shopFuncShortcuts];
      let hasChanged = false;
      newFuncShortcuts.forEach((item, idx) => {
        if (item.key === value) {
          newFuncShortcuts[idx].key = undefined;
          hasChanged = true;
        }
      });
      if (hasChanged) {
        setShopFuncShortcuts(newFuncShortcuts);
        saveShortcuts('shopFunc', newFuncShortcuts);
      }
    }

    setShopMenuShortcuts(newShortcuts);
    saveShortcuts('shopMenu', newShortcuts);
  };

  // 添加门店功能快捷键
  const addShopFuncShortcut = () => {
    const newShortcuts = [...shopFuncShortcuts, { func: undefined, key: undefined }];
    setShopFuncShortcuts(newShortcuts);
  };

  // 删除门店功能快捷键
  const deleteShopFuncShortcut = (index) => {
    const newShortcuts = [...shopFuncShortcuts];
    newShortcuts.splice(index, 1);
    setShopFuncShortcuts(newShortcuts);
    saveShortcuts('shopFunc', newShortcuts);
  };

  // 更新门店功能快捷键
  const updateShopFuncShortcut = (index, field, value) => {
    const newShortcuts = [...shopFuncShortcuts];
    newShortcuts[index][field] = value;

    // 如果修改的是快捷键字段，需要检查是否与其他快捷键冲突
    if (field === 'key' && value) {
      // 在门店功能快捷键中检查冲突
      newShortcuts.forEach((item, idx) => {
        if (idx !== index && item.key === value) {
          newShortcuts[idx].key = undefined;
        }
      });

      // 在门店菜单快捷键中检查冲突
      const newMenuShortcuts = [...shopMenuShortcuts];
      let hasChanged = false;
      newMenuShortcuts.forEach((item, idx) => {
        if (item.key === value) {
          newMenuShortcuts[idx].key = undefined;
          hasChanged = true;
        }
      });
      if (hasChanged) {
        setShopMenuShortcuts(newMenuShortcuts);
        saveShortcuts('shopMenu', newMenuShortcuts);
      }
    }

    setShopFuncShortcuts(newShortcuts);
    saveShortcuts('shopFunc', newShortcuts);
  };

  // 添加集团菜单快捷键
  const addGroupMenuShortcut = () => {
    const newShortcuts = [...groupMenuShortcuts, { menu: undefined, key: undefined }];
    setGroupMenuShortcuts(newShortcuts);
  };

  // 删除集团菜单快捷键
  const deleteGroupMenuShortcut = (index) => {
    const newShortcuts = [...groupMenuShortcuts];
    newShortcuts.splice(index, 1);
    setGroupMenuShortcuts(newShortcuts);
    saveShortcuts('groupMenu', newShortcuts);
  };

  // 更新集团菜单快捷键
  const updateGroupMenuShortcut = (index, field, value) => {
    const newShortcuts = [...groupMenuShortcuts];
    newShortcuts[index][field] = value;

    // 如果修改的是快捷键字段，需要检查是否与其他快捷键冲突
    if (field === 'key' && value) {
      // 在集团菜单快捷键中检查冲突
      newShortcuts.forEach((item, idx) => {
        if (idx !== index && item.key === value) {
          newShortcuts[idx].key = undefined;
        }
      });
    }

    setGroupMenuShortcuts(newShortcuts);
    saveShortcuts('groupMenu', newShortcuts);
  };

  return (
    <div className={styles.quickKeyContainer}>
      <div className={styles.pageTitle}>{intl.get('app_custom_quick_entry_shortcut_key')}</div>
      <div className={styles.tipText}>
        <InfoCircleFilled className={styles.tipIcon} />
        <span>{intl.get('app_custom_quick_entry_only_current_account_tips')}</span>
      </div>

      {/* 门店界面菜单快捷键 */}
      {hasStoreAuth && (
        <>
          <div className={styles.sectionTitle}>
            {intl.get('app_custom_quick_entry_store_menu_tips')}
          </div>
          {shopMenuShortcuts.map((shortcut, index) => (
            <div
              key={`shop-menu-shortcut-${shortcut.key || index}`}
              className={styles.formItemWrap}
            >
              <div className={styles.formItem}>
                <Cascader
                  options={filterMenuKey(menus)}
                  placeholder={intl.get('app_custom_quick_entry_select_menu')}
                  value={shortcut.menu}
                  onChange={(value) => updateShopMenuShortcut(index, 'menu', value)}
                  expandTrigger="hover"
                  allowClear={false}
                  fieldNames={{ label: 'name', value: 'id' }}
                  displayRender={(labels) => labels.join(' - ')}
                />
              </div>
              <div className={styles.formItem}>
                <Select
                  placeholder={intl.get('app_custom_quick_entry_select')}
                  value={shortcut.key}
                  onChange={(value) => updateShopMenuShortcut(index, 'key', value)}
                >
                  {FUNCTION_SHORTCUT_KEYS.map((key) => (
                    <Select.Option key={key} value={key}>
                      {key}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <CloseOutlined
                className={styles.deleteBtn}
                onClick={() => deleteShopMenuShortcut(index)}
              />
            </div>
          ))}
          <Button type="primary" ghost onClick={addShopMenuShortcut} className={styles.addBtn}>
            {intl.get('app_custom_quick_entry_add')}
          </Button>
          {/* 门店界面功能快捷键 */}
          <div className={styles.sectionTitle}>
            {intl.get('app_custom_quick_entry_store_function_tips')}
          </div>
          {shopFuncShortcuts.map((shortcut, index) => (
            <div
              key={`shop-func-shortcut-${shortcut.key || index}`}
              className={styles.formItemWrap}
            >
              <div className={styles.formItem}>
                <Select
                  placeholder={intl.get('app_custom_quick_entry_select_function')}
                  value={shortcut.func}
                  onChange={(value) => updateShopFuncShortcut(index, 'func', value)}
                >
                  {SHOP_FUNC_LIST.map((func) => (
                    <Select.Option key={func.value} value={func.value}>
                      {func.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <div className={styles.formItem}>
                <Select
                  placeholder={intl.get('app_custom_quick_entry_select')}
                  value={shortcut.key}
                  onChange={(value) => updateShopFuncShortcut(index, 'key', value)}
                >
                  {FUNCTION_SHORTCUT_KEYS.map((key) => (
                    <Select.Option key={key} value={key}>
                      {key}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <CloseOutlined
                className={styles.deleteBtn}
                onClick={() => deleteShopFuncShortcut(index)}
              />
            </div>
          ))}
          <Button type="primary" ghost onClick={addShopFuncShortcut} className={styles.addBtn}>
            {intl.get('app_custom_quick_entry_add')}
          </Button>
        </>
      )}

      {/* 集团界面功能快捷键 */}
      {hasGroupAuth && (
        <>
          <div className={styles.sectionTitle}>
            {intl.get('app_custom_quick_entry_group_function_tips')}
          </div>
          {groupMenuShortcuts.map((shortcut, index) => (
            <div
              key={`group-menu-shortcut-${shortcut.key || index}`}
              className={styles.formItemWrap}
            >
              <div className={styles.formItem}>
                <Cascader
                  options={companyGroup?.menus || []}
                  placeholder={intl.get('app_custom_quick_entry_select_menu')}
                  value={shortcut.menu}
                  onChange={(value) => updateGroupMenuShortcut(index, 'menu', value)}
                  expandTrigger="hover"
                  fieldNames={{ label: 'name', value: 'id' }}
                  displayRender={(labels) => labels.join(' - ')}
                  allowClear={false}
                />
              </div>
              <div className={styles.formItem}>
                <Select
                  placeholder={intl.get('app_custom_quick_entry_select')}
                  value={shortcut.key}
                  onChange={(value) => updateGroupMenuShortcut(index, 'key', value)}
                >
                  {FUNCTION_SHORTCUT_KEYS.map((key) => (
                    <Select.Option key={key} value={key}>
                      {key}
                    </Select.Option>
                  ))}
                </Select>
              </div>
              <CloseOutlined
                className={styles.deleteBtn}
                onClick={() => deleteGroupMenuShortcut(index)}
              />
            </div>
          ))}
          <Button type="primary" ghost onClick={addGroupMenuShortcut} className={styles.addBtn}>
            {intl.get('app_custom_quick_entry_add')}
          </Button>
        </>
      )}

      {/* 系统快捷键 */}
      <div className={styles.sectionTitle}>{intl.get('app_custom_quick_entry_system_tips')}</div>
      <div className={styles.systemKeysItemContent}>
        <div className={styles.systemKeysItemRow}>
          <div className={styles.systemKeysItemRowTitle}>
            {intl.get('app_custom_quick_entry_refresh_page_tips')}
          </div>
          <div className={styles.systemKeysItemRowContent}>F5</div>
        </div>
        <div className={styles.systemKeysItemRow}>
          <div className={styles.systemKeysItemRowTitle}>
            {intl.get('app_custom_quick_entry_full_screen_tips')}
          </div>
          <div className={styles.systemKeysItemRowContent}>F11</div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userInfo: state.user.userInfo,
  menus: state.user.menus,
  companyGroup: state.user.companyGroup,
  shortcuts: state.user.shortcuts,
});

export default connect(mapStateToProps, { updateShortcutConfig })(QuickKey);
